import React, { useState, useCallback } from 'react';
import { Upload, X, Download, Package, Folder, Zap, Crown } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import ImagePreview from './ImagePreview';
import CompressionSettings, { CompressionConfig } from './CompressionSettings';
import FeatureComparison from './FeatureComparison';
import LimitationNotice from './LimitationNotice';
import UsageLimitNotice from './UsageLimitNotice';
import RegistrationRequiredModal from './RegistrationRequiredModal';
import { useImageFiles } from '@/hooks/useImageFiles';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { downloadFilesAsZip } from '@/utils/imageCompression';
import { validateImageFile } from '@/utils/fileValidation';
import { useAnalytics } from '@/hooks/useAnalytics';

const ImageUploader = () => {
  const [isDragActive, setIsDragActive] = useState(false);
  const [isDownloadingAll, setIsDownloadingAll] = useState(false);
  // const [isDownloadingBackgroundRemoval, setIsDownloadingBackgroundRemoval] = useState(false); // TODO: Descomentar cuando esté completo
  const [showSettings, setShowSettings] = useState(false);
  const [compressionConfig, setCompressionConfig] = useState<CompressionConfig>({
    quality: 0.7,
    preset: 'medium',
    autoResize: false,
    maxWidth: undefined,
    maxHeight: undefined
  });
  const { toast } = useToast();
  const { isPremium } = useSubscription();
  const { trackEvent } = useAnalytics();
  const { 
    filesData, 
    addFiles, 
    removeFile, 
    clearFiles, 
    compressFile,
    compressAllFiles,
    retryFile,
    // TODO: Descomentar cuando la funcionalidad esté completa
    // removeBackgroundFile,
    // removeBackgroundAllFiles,
    getFailedFiles,
    getCompletedFiles,
    canAddFiles,
    getRemainingSlots,
    maxFiles,
    maxRetryAttempts,
    usageData
  } = useImageFiles(isPremium);

  const validateFile = (file: File) => {
    // All users can upload JPEG, PNG and WebP
    if (!file.type.startsWith('image/jpeg') && 
        !file.type.startsWith('image/jpg') && 
        !file.type.startsWith('image/png') && 
        !file.type.startsWith('image/webp')) {
      return false;
    }
    
    // Premium users have higher file size limits
    const maxSize = isPremium ? 50 * 1024 * 1024 : 10 * 1024 * 1024; // 50MB vs 10MB
    if (file.size > maxSize) {
      return false;
    }
    return true;
  };

  const handleFiles = useCallback(async (fileList: FileList) => {
    const validFiles: File[] = [];
    const invalidFiles: string[] = [];
    const totalFiles = Array.from(fileList);

    // Verificar límite de archivos
    const remainingSlots = getRemainingSlots();
    if (remainingSlots === 0) {
      toast({
        title: "Límite alcanzado",
        description: isPremium ? 
          `Ya tienes muchas imágenes. Elimina algunas para agregar más.` :
          `Límite gratuito alcanzado (${typeof maxFiles === 'number' ? maxFiles : 'ilimitado'}). Hazte Premium para más imágenes.`,
        variant: "destructive",
      });
      return;
    }

    totalFiles.forEach(file => {
      if (validateFile(file)) {
        validFiles.push(file);
      } else {
        invalidFiles.push(file.name);
      }
    });

    // Limitar archivos válidos según slots disponibles para usuarios gratuitos
    const filesToAdd = isPremium || remainingSlots === Infinity ? 
      validFiles : 
      validFiles.slice(0, remainingSlots);
    const exceedingFiles = validFiles.length - filesToAdd.length;

    if (filesToAdd.length > 0) {
      await addFiles(filesToAdd);
      trackEvent('upload', { count: filesToAdd.length });
      toast({
        title: "Imágenes agregadas",
        description: `${filesToAdd.length} imagen(es) agregada(s). Haz clic en "Comprimir" para procesarlas.`,
      });
    }

    if (exceedingFiles > 0) {
      toast({
        title: "Límite de archivos",
        description: isPremium ?
          `${exceedingFiles} archivo(s) no se pudieron agregar.` :
          `${exceedingFiles} archivo(s) no se pudieron agregar. Hazte Premium para más imágenes.`,
        variant: "destructive",
      });
    }

    if (invalidFiles.length > 0) {
      const supportedFormats = "JPEG, PNG, WebP";
      const maxSizeText = isPremium ? "50MB" : "10MB";
      toast({
        title: "Archivos no válidos",
        description: `${invalidFiles.length} archivo(s) no son ${supportedFormats} válidos o exceden ${maxSizeText}.`,
        variant: "destructive",
      });
    }
  }, [addFiles, toast, getRemainingSlots, maxFiles, isPremium]);

  const handleCompressAll = async () => {
    // Check if anonymous user needs to register
    if (!isPremium && usageData.requiresRegistration()) {
      usageData.setShowRegistrationModal(true);
      return;
    }

    const pendingFiles = filesData.filter(f => f.status === 'pending');
    if (pendingFiles.length === 0) {
      toast({
        title: "Sin archivos pendientes",
        description: "No hay imágenes pendientes para comprimir.",
        variant: "destructive",
      });
      return;
    }

    await compressAllFiles(compressionConfig);
    trackEvent('compress', { count: pendingFiles.length, quality: compressionConfig.quality });
  };

  const downloadAll = async () => {
    const compressedFiles = getCompletedFiles().filter(fileData => fileData.compression);
    
    if (compressedFiles.length === 0) {
      toast({
        title: "Sin archivos comprimidos",
        description: "No hay imágenes comprimidas para descargar.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDownloadingAll(true);
      const filesToDownload = compressedFiles.map(fileData => fileData.compression!.compressedFile);
      const zipName = `imagenes_comprimidas_${new Date().toISOString().split('T')[0]}.zip`;
      
      await downloadFilesAsZip(filesToDownload, zipName);
      trackEvent('download', { count: compressedFiles.length });
      
      toast({
        title: "Descarga completada",
        description: `${compressedFiles.length} imagen(es) descargada(s) en ${zipName}.`,
      });
    } catch (error) {
      console.error('Error al descargar archivos:', error);
      toast({
        title: "Error en la descarga",
        description: "No se pudo crear el archivo ZIP. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingAll(false);
    }
  };

  /* TODO: Descomentar cuando la funcionalidad esté completa
  const downloadAllBackgroundRemoved = async () => {
    const backgroundRemovedFiles = filesData.filter(fileData => fileData.backgroundRemoval);
    
    if (backgroundRemovedFiles.length === 0) {
      toast({
        title: "Sin imágenes sin fondo",
        description: "No hay imágenes sin fondo para descargar.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsDownloadingBackgroundRemoval(true);
      const filesToDownload = backgroundRemovedFiles.map(fileData => fileData.backgroundRemoval!.processedFile);
      const zipName = `imagenes_sin_fondo_${new Date().toISOString().split('T')[0]}.zip`;
      
      await downloadFilesAsZip(filesToDownload, zipName);
      
      toast({
        title: "Descarga completada",
        description: `${backgroundRemovedFiles.length} imagen(es) PNG sin fondo descargada(s) - 100% calidad para sublimación.`,
      });
    } catch (error) {
      console.error('Error al descargar archivos sin fondo:', error);
      toast({
        title: "Error en la descarga",
        description: "No se pudo crear el archivo ZIP. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsDownloadingBackgroundRemoval(false);
    }
  };
  */

  const onDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
    
    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles.length > 0) {
      handleFiles(droppedFiles);
    }
  }, [handleFiles]);

  const onDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(true);
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragActive(false);
  }, []);

  const onInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
  }, [handleFiles]);

  const onFolderSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = e.target.files;
    if (selectedFiles && selectedFiles.length > 0) {
      handleFiles(selectedFiles);
    }
  }, [handleFiles]);

  const failedFiles = getFailedFiles();
  const completedFiles = getCompletedFiles();
  const pendingFiles = filesData.filter(f => f.status === 'pending');
  // const backgroundRemovedFiles = filesData.filter(f => f.backgroundRemoval); // TODO: Descomentar cuando esté completo
  const remainingSlots = getRemainingSlots();
  const processingFiles = filesData.filter(f => f.isCompressing);
  const supportedFormats = "JPEG, PNG, WebP";
  const maxSizeText = isPremium ? "50MB" : "10MB";
  
  // Get numeric values for display purposes
  const maxFilesNumeric = typeof maxFiles === 'number' ? maxFiles : null;
  const currentFileCount = filesData.length;

  // Calculate boolean values for disabled states
  const isUploadDisabled = remainingSlots === 0 || (!isPremium && usageData.hasReachedLimit());
  const canProcessPendingFiles = !isPremium ? usageData.canProcessFiles(pendingFiles.length) : true;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Usage Limit Notice for Non-Premium Users */}
      {!isPremium && (
        <UsageLimitNotice
          remainingFiles={usageData.remainingFiles}
          processedFiles={usageData.dailyUsage.processedFiles}
          freeLimit={usageData.freeLimit}
          hasReachedLimit={usageData.hasReachedLimit()}
        />
      )}

      {/* Limitation Notices */}
      {!isPremium && filesData.length > 0 && (
        <div className="mb-6 space-y-3">
          {maxFilesNumeric && currentFileCount >= maxFilesNumeric * 0.8 && (
            <LimitationNotice 
              type="fileCount" 
              current={currentFileCount} 
              max={maxFilesNumeric}
            />
          )}
          <LimitationNotice type="fileSize" />
        </div>
      )}

      {/* Upload Area */}
      <div
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300
          ${isDragActive 
            ? 'border-blue-500 bg-blue-50 scale-105' 
            : isUploadDisabled
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
            : isPremium
            ? 'border-purple-300 bg-purple-50 hover:border-purple-400 hover:bg-purple-100'
            : 'border-gray-300 bg-white hover:border-blue-400 hover:bg-blue-50'
          }
        `}
      >
        <input
          type="file"
          multiple
          accept="image/jpeg,image/jpg,image/png,image/webp"
          onChange={onInputChange}
          disabled={isUploadDisabled}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
        
        <div className="space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors duration-300 ${
            isDragActive ? 'bg-blue-500' : isUploadDisabled ? 'bg-gray-300' : isPremium ? 'bg-purple-500' : 'bg-gray-100'
          }`}>
            {isPremium ? (
              <Crown className={`w-8 h-8 ${isDragActive || !isUploadDisabled ? 'text-white' : 'text-gray-400'}`} />
            ) : (
              <Upload className={`w-8 h-8 ${
                isDragActive ? 'text-white' : isUploadDisabled ? 'text-gray-400' : 'text-gray-500'
              }`} />
            )}
          </div>
          
          <div>
            <h3 className={`text-xl font-semibold mb-2 ${
              isUploadDisabled ? 'text-gray-400' : 'text-gray-700'
            }`}>
              {isUploadDisabled
                ? (!isPremium && usageData.hasReachedLimit()) 
                  ? 'Límite diario de compresión alcanzado'
                  : 'Límite de imágenes alcanzado'
                : isDragActive 
                ? '¡Suelta las imágenes aquí!' 
                : isPremium
                ? `Arrastra y suelta tus imágenes ${supportedFormats}`
                : `Arrastra y suelta tus imágenes ${supportedFormats}`
              }
            </h3>
            <p className={`${isUploadDisabled ? 'text-gray-400' : 'text-gray-500'}`}>
              {isUploadDisabled
                ? (!isPremium && usageData.hasReachedLimit())
                  ? `Has procesado ${usageData.dailyUsage.processedFiles}/${usageData.freeLimit} imágenes hoy`
                  : `Elimina algunas imágenes para agregar más (${currentFileCount}/${maxFiles})`
                : 'O haz clic para seleccionar archivos'
              }
            </p>
            <p className="text-sm text-gray-400 mt-2">
              Máximo {maxSizeText} por archivo • Solo archivos {supportedFormats}
            </p>
            {remainingSlots > 0 && !(!isPremium && usageData.hasReachedLimit()) && (
              <p className={`text-sm mt-1 ${isPremium ? 'text-purple-600' : 'text-blue-600'}`}>
                {isPremium ? 
                  'Puedes agregar imágenes ilimitadas' :
                  `Puedes agregar ${remainingSlots} imagen(es) más`
                }
                {isPremium && <span className="ml-2 inline-flex items-center"><Crown className="w-3 h-3 mr-1" />Premium</span>}
              </p>
            )}
          </div>

          {/* Folder Selection Button */}
          {remainingSlots > 0 && !(!isPremium && usageData.hasReachedLimit()) && (
            <div className="mt-4">
              <label className={`inline-flex items-center gap-2 px-4 py-2 rounded-lg transition-colors cursor-pointer ${
                isPremium 
                  ? 'bg-purple-600 hover:bg-purple-700 text-white'
                  : 'bg-blue-600 hover:bg-blue-700 text-white'
              }`}>
                <Folder className="w-4 h-4" />
                Seleccionar Carpeta
                <input
                  type="file"
                  {...({ webkitdirectory: "" } as any)}
                  multiple
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={onFolderSelect}
                  className="hidden"
                />
              </label>
            </div>
          )}
        </div>
      </div>

      {/* Compression Settings */}
      {filesData.length > 0 && (
        <div className="mt-8">
          <CompressionSettings
            config={compressionConfig}
            onConfigChange={setCompressionConfig}
            isOpen={showSettings}
            onToggle={() => setShowSettings(!showSettings)}
            isPremium={isPremium}
          />
        </div>
      )}

      {/* Status Summary */}
      {filesData.length > 0 && (
        <div className="mt-8">
          <div className="bg-white rounded-lg p-6 shadow-sm border">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-semibold text-gray-700 flex items-center gap-2">
                Mis Imágenes ({currentFileCount}/{maxFiles})
                {isPremium && <Crown className="w-5 h-5 text-purple-500" />}
                {!isPremium && maxFilesNumeric && currentFileCount >= maxFilesNumeric * 0.8 && (
                  <span className="text-sm bg-orange-100 text-orange-700 px-2 py-1 rounded-full">
                    Cerca del límite
                  </span>
                )}
              </h2>
              <div className="flex gap-3">
                {pendingFiles.length > 0 && canProcessPendingFiles && (
                  <Button
                    onClick={handleCompressAll}
                    disabled={processingFiles.length > 0}
                    className={isPremium ? "bg-purple-600 hover:bg-purple-700" : "bg-green-600 hover:bg-green-700"}
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    {processingFiles.length > 0 ? 'Comprimiendo...' : `Comprimir (${pendingFiles.length})`}
                  </Button>
                )}
                
                {completedFiles.length > 0 && (
                  <Button
                    onClick={downloadAll}
                    disabled={isDownloadingAll}
                    variant="outline"
                    className="flex items-center gap-2"
                  >
                    <Package className="w-4 h-4" />
                    {isDownloadingAll ? 'Creando ZIP...' : `Descargar ZIP (${completedFiles.length})`}
                  </Button>
                )}

                {/* TODO: Descomentar cuando la funcionalidad esté completa
                {backgroundRemovedFiles.length > 0 && (
                  <Button
                    onClick={downloadAllBackgroundRemoved}
                    disabled={isDownloadingBackgroundRemoval}
                    className="flex items-center gap-2 bg-gradient-to-r from-pink-500 to-purple-600 hover:from-pink-600 hover:to-purple-700 text-white"
                  >
                    <Wand2 className="w-4 h-4" />
                    {isDownloadingBackgroundRemoval ? 'Creando ZIP...' : `PNG Sin Fondo (${backgroundRemovedFiles.length})`}
                  </Button>
                )}
                */}
                
                <button
                  onClick={clearFiles}
                  className="flex items-center gap-2 px-4 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors duration-200"
                >
                  <X className="w-4 h-4" />
                  Limpiar todo
                </button>
              </div>
            </div>

            {/* Status Stats */}
            <div className="grid grid-cols-4 gap-4 mb-4">
              <div className="text-center p-3 bg-yellow-50 rounded-lg">
                <span className="block text-2xl font-bold text-yellow-600">{pendingFiles.length}</span>
                <span className="text-sm text-yellow-700">Pendientes</span>
              </div>
              <div className="text-center p-3 bg-blue-50 rounded-lg">
                <span className="block text-2xl font-bold text-blue-600">{processingFiles.length}</span>
                <span className="text-sm text-blue-700">Procesando</span>
              </div>
              <div className="text-center p-3 bg-green-50 rounded-lg">
                <span className="block text-2xl font-bold text-green-600">{completedFiles.length}</span>
                <span className="text-sm text-green-700">Completadas</span>
              </div>
              <div className="text-center p-3 bg-red-50 rounded-lg">
                <span className="block text-2xl font-bold text-red-600">{failedFiles.length}</span>
                <span className="text-sm text-red-700">Fallidas</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Files Grid */}
      {filesData.length > 0 && (
        <div className="mt-8">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
            {filesData.map((fileData, index) => (
              <ImagePreview
                key={`${fileData.file.name}-${index}`}
                file={fileData.file}
                compression={fileData.compression}
                isCompressing={fileData.isCompressing}
                status={fileData.status}
                error={fileData.error}
                retryCount={fileData.retryCount}
                maxRetryAttempts={maxRetryAttempts}
                onRemove={() => removeFile(index)}
                onRetry={() => retryFile(index, compressionConfig)}
                onCompress={() => compressFile(index, compressionConfig)}
                compressionConfig={compressionConfig}
                isPremium={isPremium}
              />
            ))}
          </div>
        </div>
      )}

      {/* Feature Comparison - Show only when no files are uploaded */}
      {!isPremium && (
        <div className="mt-8">
          <FeatureComparison />
        </div>
      )}
      {/* Registration Required Modal */}
      <RegistrationRequiredModal
        open={usageData.showRegistrationModal}
        onOpenChange={usageData.setShowRegistrationModal}
        anonymousLimit={usageData.anonymousLimit}
      />
    </div>
  );
};

export default ImageUploader;
